// src/components/ProductTable.jsx
import React, { useMemo, useState, useEffect } from "react";
import GenericTable from "./GenericTable";
import { Save, Close } from "@mui/icons-material";
import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import { useCategories } from "../hooks/useCategories";
import { useTranslation } from "react-i18next";
import {
  readWorkspaceLocalJson,
  writeWorkspaceLocalJson,
  WS_KEY,
} from "../utils/workspaceStorage";

// Permissões
import { useEffectivePermissions, toSet, can } from "../hooks/useEffectivePermissions";

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

/* ==== Helpers ==== */
const toBRL = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/* Visíveis por padrão (catálogo genérico) */
const DEFAULT_VISIBLE = [
  "sku",
  "nome",
  "preco",
  "preco_comdesconto",
  "data_ultima_alteracao",
  "estoque",
  "status",
  "actions",
];

/* Visíveis por padrão — transporte rodoviário (trechos) */
const DEFAULT_VISIBLE_PASSENGER = [
  "origem_nome",
  "destino_nome",
  "fonte",
  "preco",
  "preco_comdesconto",
  "data_ultima_alteracao",
  "status",
  "destaque",
  "actions",
];

/* Todas as colunas (IDs foram mantidos fora das visíveis) */
const ALL_COLUMNS = [
  { field: "id", label: "ID" },
  { field: "sku", label: "SKU" },
  { field: "nome", label: "Nome" },
  { field: "tipo", label: "Tipo" },
  { field: "projeto_nome", label: "Empresa" }, // <-- NOVO
  { field: "categoria_nome", label: "Categoria" },
  { field: "subcategoria_nome", label: "Subcategoria" },
  { field: "id_categoria", label: "Categoria (ID)" },
  { field: "id_subcategoria", label: "Subcategoria (ID)" },
  { field: "preco", label: "Preço sugerido" },
  { field: "preco_comdesconto", label: "Preço concessionário" },
  { field: "estoque", label: "Estoque" },
  { field: "peso", label: "Peso (kg)" },
  { field: "largura", label: "Largura (cm)" },
  { field: "altura", label: "Altura (cm)" },
  { field: "comprimento", label: "Comprimento (cm)" },
  { field: "destaque", label: "Destaque" },
  { field: "status", label: "Status" },
  { field: "data_cadastro", label: "Data Cadastro" },
  { field: "data_ultima_alteracao", label: "Última atualização" },
  { field: "actions", label: "Ações" },
];

const ALL_COLUMNS_PASSENGER = [
  { field: "origem_nome", label: "Origem" },
  { field: "destino_nome", label: "Destino" },
  { field: "nome", label: "Nome do trecho" },
  { field: "fonte", label: "Fonte (ERP)" },
  { field: "sku", label: "SKU" },
  { field: "projeto_nome", label: "Empresa" },
  { field: "categoria_nome", label: "Categoria" },
  { field: "subcategoria_nome", label: "Subcategoria" },
  { field: "preco", label: "Preço referência" },
  { field: "preco_comdesconto", label: "Preço promocional" },
  { field: "max_desconto", label: "Desconto máximo (%)" },
  { field: "aceitacarga", label: "Aceita cargas" },
  { field: "distancia", label: "Distância (km)" },
  { field: "destaque", label: "Destaque" },
  { field: "status", label: "Status" },
  { field: "data_cadastro", label: "Data Cadastro" },
  { field: "data_ultima_alteracao", label: "Última atualização" },
  { field: "actions", label: "Ações" },
];

function formatCityCell(nome, uf) {
  if (!nome) return "—";
  const u = uf ? String(uf).trim() : "";
  return u ? `${nome} — ${u}` : nome;
}

const FILTERS_DEFAULT = {
  status: "all",
  tipo: "all",
  precoMin: "",
  precoMax: "",
  estoqueMin: "",
  estoqueMax: "",
  id_categoria: "",
  id_subcategoria: "",
};

const FILTERS_DEFAULT_PASSENGER = {
  status: "all",
  fonte: "",
  id_origem: "",
  id_destino: "",
  destaque: "all",
  aceitacarga: "all",
  precoMin: "",
  precoMax: "",
  id_categoria: "",
};

const ProductTable = ({
  products = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  searchValue,
  onSearchChange,
  onEditProduct,
  onDeleteProduct,
  isLoading,

  // bulk:
  onBulkUpdate,
  onReload,
  onDeleteSelected,

  // Filtros avançados (opcionais externos; senão, fallback local)
  filters,
  onFiltersChange,
  onFiltersApply,
  onFiltersClear,

  /** Ordenação server-side (ex.: lista de produtos + API sort_by). */
  sortingMode,
  sortModel,
  onSortModelChange,

  /** Segmento transporte rodoviário: colunas e rótulos de trecho. */
  isPassengerTransport = false,
}) => {
  const { t } = useTranslation("products");
  /* ===== Permissões ===== */
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);

  const canEditPrice = can(scopeSet, "products_price", "update");
  const canEditStock = can(scopeSet, "products_stock", "update");
  const canEditAnyPriceStock = canEditPrice || canEditStock;

  const canUpdateProduct = can(scopeSet, "products", "update");
  const canDeleteProduct = can(scopeSet, "products", "delete");

  const columnCatalog = isPassengerTransport ? ALL_COLUMNS_PASSENGER : ALL_COLUMNS;
  const defaultVisible = useMemo(
    () => (isPassengerTransport ? DEFAULT_VISIBLE_PASSENGER : DEFAULT_VISIBLE),
    [isPassengerTransport]
  );
  const visibleStorageKey = isPassengerTransport
    ? WS_KEY.PRODUCT_TABLE_VISIBLE_PASSENGER
    : WS_KEY.PRODUCT_TABLE_VISIBLE;

  /* ===== Carrega categorias/subcategorias ===== */
  const {
    rootCategories = [],
    isLoading: categoriesLoading,
  } = useCategories({ page: 1, limit: 500 });

  // Mapa rápido de id -> nome e id -> subcats[]
  const catMap = useMemo(() => {
    const map = new Map();
    (rootCategories || []).forEach((c) => map.set(String(c.id), c));
    return map;
  }, [rootCategories]);

  const getCategoryName = (id) => {
    if (id == null) return "—";
    const c = catMap.get(String(id));
    return c?.nome || "—";
  };

  const getSubCategoryName = (catId, subId) => {
    if (subId == null) return "—";
    const c = catMap.get(String(catId));
    const sub = (c?.children || []).find((s) => String(s.id) === String(subId));
    return sub?.nome || "—";
  };

  const getSubcatsOf = (catId) => {
    if (!catId) return [];
    const c = catMap.get(String(catId));
    return c?.children || [];
  };

  /* ===== Colunas visíveis (persistidas) ===== */
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = readWorkspaceLocalJson(visibleStorageKey, null);
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return defaultVisible;
  });

  useEffect(() => {
    try {
      const saved = readWorkspaceLocalJson(visibleStorageKey, null);
      if (Array.isArray(saved) && saved.length) setVisibleColumns(saved);
      else setVisibleColumns(defaultVisible);
    } catch {
      setVisibleColumns(defaultVisible);
    }
  }, [isPassengerTransport, visibleStorageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    writeWorkspaceLocalJson(visibleStorageKey, visibleColumns);
  }, [visibleColumns, visibleStorageKey]);

  /* ===== Larguras (persistidas) ===== */
  const [widths, setWidths] = useState(() => {
    try {
      return readWorkspaceLocalJson(WS_KEY.PRODUCT_TABLE_WIDTHS, {}) || {};
    } catch {}
    return {};
  });
  useEffect(() => {
    writeWorkspaceLocalJson(WS_KEY.PRODUCT_TABLE_WIDTHS, widths);
  }, [widths]);

  const handleColumnWidthChange = (params) => {
    const field = params?.colDef?.field;
    const width = params?.width;
    if (!field || !width) return;
    setWidths((w) => ({ ...w, [field]: width }));
  };

  /* ===== Edição em massa ===== */
  const [massEditOn, setMassEditOn] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  // Desliga edição em massa se usuário não tiver permissão
  useEffect(() => {
    if (!canEditAnyPriceStock && massEditOn) {
      setMassEditOn(false);
    }
  }, [canEditAnyPriceStock, massEditOn]);

  // feedback
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [resultDialog, setResultDialog] = useState({
    open: false,
    results: [],
  });

  useEffect(() => {
    if (massEditOn) {
      const init = {};
      products.forEach((p) => {
        init[p.id] = {
          preco: p.preco ?? "",
          preco_comdesconto: p.preco_comdesconto ?? "",
          estoque: p.estoque ?? "",
          status: p.status == null ? 0 : Number(p.status),
        };
      });
      setDrafts(init);
    } else {
      setDrafts({});
    }
  }, [massEditOn, products]);

  /* ===== Rows ===== */
  const toNum = (v) => (v == null || v === "" ? null : Number(v));
  const fmtKg = (v) =>
    v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}`;
  const fmtCm = (v) =>
    v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(1)}`;

  const tipoLabel = (tipoVal) => {
    switch (Number(tipoVal)) {
      case 1:
        return t("table.tipo1");
      case 2:
        return t("table.tipo2");
      case 3:
        return t("table.tipo3");
      case 4:
        return t("table.tipo4");
      case 5:
        return t("table.tipo5");
      default:
        return "—";
    }
  };

  const statusLabelCell = (v) =>
    Number(v) === 1 ? t("table.statusActive") : t("table.statusInactive");

  const menuColumnLabel = (field) => {
    const m = {
      id: "colId",
      sku: "colSku",
      nome: isPassengerTransport ? "trechoTable.colRouteName" : "colNome",
      tipo: "colTipo",
      projeto_nome: "colEmpresa",
      categoria_nome: "colCategoria",
      subcategoria_nome: "colSubcategoria",
      id_categoria: "colCategoriaId",
      id_subcategoria: "colSubcategoriaId",
      origem_nome: "trechoTable.colOrigin",
      destino_nome: "trechoTable.colDestination",
      fonte: "trechoTable.colSource",
      aceitacarga: "trechoTable.colAcceptsCargo",
      distancia: "trechoTable.colDistance",
      max_desconto: "trechoTable.colMaxDiscount",
      preco: isPassengerTransport ? "trechoTable.colReferencePrice" : "colPrecoSugerido",
      preco_comdesconto: isPassengerTransport
        ? "trechoTable.colPromoPrice"
        : "colPrecoConcessionaria",
      estoque: "colEstoque",
      peso: "colPeso",
      largura: "colLargura",
      altura: "colAltura",
      comprimento: "colComprimento",
      destaque: "colDestaque",
      status: "colStatus",
      data_cadastro: "colDataCadastro",
      data_ultima_alteracao: "colUltimaAtualizacao",
      actions: "colAcoes",
    };
    const k = m[field];
    return k ? t(`table.${k}`) : field;
  };

  const rows = useMemo(
    () =>
      products.map((p) => {
        const categoria_nome = getCategoryName(p.id_categoria);
        const subcategoria_nome = getSubCategoryName(p.id_categoria, p.id_subcategoria);

        // Tenta deduzir o nome do projeto/empresa a partir dos campos comuns
        const projeto_nome =
          p.projeto_nome ||
          p.nome_projeto ||
          p.empresa_nome ||
          p.company_name ||
          p.projeto ||
          p.empresa ||
          "—";

        return {
          id: p.id,
          originalProduct: p,
          sku: p.sku || "—",
          nome: p.nome || "—",
          tipo: tipoLabel(p.tipo),
          origem_nome: formatCityCell(p.origem_nome, p.origem_uf),
          destino_nome: formatCityCell(p.destino_nome, p.destino_uf),
          fonte: (p.fonte && String(p.fonte).trim()) || "—",
          aceitacarga: Number(p.aceitacarga) === 1 ? t("trechoTable.yes") : t("trechoTable.no"),
          distancia: p.distancia != null && String(p.distancia).trim() !== "" ? String(p.distancia) : "—",
          max_desconto:
            p.max_desconto != null && Number.isFinite(Number(p.max_desconto))
              ? `${Number(p.max_desconto)}%`
              : "—",

          // Empresa / Projeto
          projeto_nome,

          // preço
          preco: toNum(p.preco),
          preco_fmt: toBRL(p.preco),
          preco_comdesconto: toNum(p.preco_comdesconto),
          preco_comdesconto_fmt: toBRL(p.preco_comdesconto),

          // estoque
          estoque: toNum(p.estoque),

          // categoria / subcategoria
          id_categoria: p.id_categoria ?? null,
          id_subcategoria: p.id_subcategoria ?? null,
          categoria_nome,
          subcategoria_nome,

          // dimensões/peso
          peso: toNum(p.peso),
          largura: toNum(p.largura),
          altura: toNum(p.altura),
          comprimento: toNum(p.comprimento),

          // flags
          status: p.status == null ? 0 : Number(p.status),
          destaque: p.destaque ? 1 : 0,

          data_ultima_alteracao:
            p.data_ultima_alteracao ||
            p.updatedAt ||
            p.updated_at ||
            null,
          data_cadastro: p.data_cadastro || "—",
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, catMap, t, isPassengerTransport]
  );

  /* ===== Detecta mudanças ===== */
  const changedRows = useMemo(() => {
    if (!massEditOn) return [];
    return products
      .filter((p) => {
        const d = drafts[p.id];
        if (!d) return false;

        const precoChanged =
          canEditPrice &&
          d.preco !== "" &&
          Number(d.preco) !== Number(p.preco ?? 0);

        const finalChanged =
          canEditPrice &&
          d.preco_comdesconto !== "" &&
          Number(d.preco_comdesconto) !== Number(p.preco_comdesconto ?? 0);

        const estoqueChanged =
          !isPassengerTransport &&
          canEditStock &&
          d.estoque !== "" &&
          Number(d.estoque) !== Number(p.estoque ?? 0);

        const statusChanged =
          typeof d.status !== "undefined" &&
          Number(d.status) !== Number(p.status == null ? 0 : p.status);

        return precoChanged || finalChanged || estoqueChanged || statusChanged;
      })
      .map((p) => ({
        id: p.id,
        nome: p.nome,
        before: {
          preco: p.preco ?? null,
          preco_comdesconto: p.preco_comdesconto ?? null,
          estoque: isPassengerTransport ? null : p.estoque ?? null,
          status: p.status == null ? 0 : Number(p.status),
        },
        after: {
          preco: drafts[p.id]?.preco === "" ? null : Number(drafts[p.id]?.preco),
          preco_comdesconto:
            drafts[p.id]?.preco_comdesconto === ""
              ? null
              : Number(drafts[p.id]?.preco_comdesconto),
          estoque: isPassengerTransport
            ? null
            : drafts[p.id]?.estoque === ""
              ? null
              : Number(drafts[p.id]?.estoque),
          status: Number(drafts[p.id]?.status),
        },
      }));
  }, [massEditOn, products, drafts, canEditPrice, canEditStock, isPassengerTransport]);

  const hasChanges = changedRows.length > 0;

  /* ===== Aplicar em massa ===== */
  const applyChanges = async () => {
    if (!hasChanges || applying) return;

    for (const row of changedRows) {
      const { preco, preco_comdesconto, estoque } = row.after;
      if (preco != null && preco < 0) return;
      if (estoque != null && estoque < 0) return;
      if (preco_comdesconto != null && preco_comdesconto < 0) return;
    }

    setApplying(true);

    const payloads = changedRows.map((r) => {
      const base = {
        id: r.id,
        preco: r.after.preco,
        preco_comdesconto: r.after.preco_comdesconto,
        status: r.after.status,
      };
      if (!isPassengerTransport) base.estoque = r.after.estoque;
      return base;
    });

    const settled = await Promise.allSettled(payloads.map((p) => onBulkUpdate(p)));

    const results = settled.map((res, idx) => {
      const base = { id: payloads[idx].id, nome: changedRows[idx].nome };
      if (res.status === "fulfilled") return { ...base, ok: true };
      const reason =
        (res.reason && (res.reason.message || res.reason.error || res.reason)) ||
        "Erro desconhecido";
      return { ...base, ok: false, error: String(reason) };
    });

    const okCount = results.filter((r) => r.ok).length;
    const err = results.filter((r) => !r.ok);

    setConfirmOpen(false);
    setMassEditOn(false);
    setApplying(false);

    await onReload?.();

    if (err.length === 0) {
      setSnack({
        open: true,
        severity: "success",
        message: `Alterações aplicadas em ${okCount} item(ns).`,
      });
    } else {
      setSnack({
        open: true,
        severity: "warning",
        message: `Aplicadas ${okCount}; falharam ${err.length}. Veja detalhes.`,
      });
      setResultDialog({ open: true, results });
    }
  };

  /* ===== Colunas ===== */
  const moneyInput = {
    size: "small",
    inputProps: { inputMode: "decimal", step: "0.01", min: 0 },
    sx: { maxWidth: 160 },
    InputProps: {
      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
    },
  };
  const intInput = {
    size: "small",
    inputProps: { inputMode: "numeric", step: "1", min: 0 },
    sx: { maxWidth: 120 },
  };

  // Coluna de Empresa/Projeto
  const empresaColumn = {
    field: "projeto_nome",
    headerName: "Empresa",
    flex: 1.2,
    minWidth: 180,
    width: widths.projeto_nome,
    renderCell: (params) => <span>{params.value || "—"}</span>,
  };

  const baseColumns = [
    ...(isPassengerTransport
      ? [
          {
            field: "origem_nome",
            headerName: t("trechoTable.colOrigin"),
            flex: 1.2,
            minWidth: 160,
            width: widths.origem_nome,
            renderCell: (params) => (
              <Typography variant="body2" noWrap title={params.value}>
                {params.value}
              </Typography>
            ),
          },
          {
            field: "destino_nome",
            headerName: t("trechoTable.colDestination"),
            flex: 1.2,
            minWidth: 160,
            width: widths.destino_nome,
            renderCell: (params) => (
              <Typography variant="body2" noWrap title={params.value}>
                {params.value}
              </Typography>
            ),
          },
          {
            field: "fonte",
            headerName: t("trechoTable.colSource"),
            flex: 0.9,
            minWidth: 110,
            width: widths.fonte,
            renderCell: (params) => (
              <Typography variant="body2" noWrap title={params.value}>
                {params.value}
              </Typography>
            ),
          },
        ]
      : []),

    // SKU
    {
      field: "sku",
      headerName: t("table.colSku"),
      flex: 1,
      minWidth: 140,
      width: widths.sku,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value}
        </Typography>
      ),
    },

    // Nome
    {
      field: "nome",
      headerName: isPassengerTransport ? t("trechoTable.colRouteName") : t("table.colNome"),
      flex: 2,
      minWidth: 240,
      width: widths.nome,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value}
        </Typography>
      ),
    },

    { field: "tipo", headerName: t("table.colTipo"), flex: 1, minWidth: 140, width: widths.tipo },

    // Categoria / Subcategoria (NOMES)
    {
      field: "categoria_nome",
      headerName: t("table.colCategoria"),
      flex: 1.2,
      minWidth: 180,
      width: widths.categoria_nome,
      sortable: false,
      renderCell: (params) => <span>{params.value || "—"}</span>,
    },
    {
      field: "subcategoria_nome",
      headerName: "Subcategoria",
      flex: 1.2,
      minWidth: 200,
      width: widths.subcategoria_nome,
      sortable: false,
      renderCell: (params) => <span>{params.value || "—"}</span>,
    },

    // (opcionais) IDs – disponíveis no menu
    { field: "id_categoria", headerName: t("table.colCategoriaId"), flex: 0.8, minWidth: 130, width: widths.id_categoria },
    { field: "id_subcategoria", headerName: t("table.colSubcategoriaId"), flex: 0.9, minWidth: 150, width: widths.id_subcategoria },

    // Preços
    {
      field: "preco",
      headerName: isPassengerTransport
        ? t("trechoTable.colReferencePrice")
        : t("table.colPrecoSugerido"),
      flex: 1,
      minWidth: 150,
      width: widths.preco,
      renderCell: (params) => {
        if (!massEditOn || !canEditPrice) return <span>{params.row.preco_fmt}</span>;
        const d = drafts[params.id] || {};
        return (
          <TextField
            {...moneyInput}
            value={d.preco ?? ""}
            onChange={(e) =>
              setDrafts((prev) => ({
                ...prev,
                [params.id]: { ...prev[params.id], preco: e.target.value },
              }))
            }
          />
        );
      },
    },
    {
      field: "preco_comdesconto",
      headerName: isPassengerTransport
        ? t("trechoTable.colPromoPrice")
        : t("table.colPrecoConcessionaria"),
      flex: 1,
      minWidth: 180,
      width: widths.preco_comdesconto,
      renderCell: (params) => {
        if (!massEditOn || !canEditPrice)
          return <span>{params.row.preco_comdesconto_fmt}</span>;
        const d = drafts[params.id] || {};
        const invalido =
          d.preco_comdesconto !== "" &&
          d.preco_comdesconto != null &&
          Number(d.preco_comdesconto) < 0;

        return (
          <TextField
            {...moneyInput}
            value={d.preco_comdesconto ?? ""}
            onChange={(e) =>
              setDrafts((prev) => ({
                ...prev,
                [params.id]: {
                  ...prev[params.id],
                  preco_comdesconto: e.target.value,
                },
              }))
            }
            error={Boolean(invalido)}
            helperText={invalido ? t("table.cannotBeNegative") : " "}
          />
        );
      },
    },

    ...(isPassengerTransport
      ? [
          {
            field: "max_desconto",
            headerName: t("trechoTable.colMaxDiscount"),
            flex: 0.8,
            minWidth: 130,
            width: widths.max_desconto,
            renderCell: (params) => <span>{params.value}</span>,
          },
          {
            field: "aceitacarga",
            headerName: t("trechoTable.colAcceptsCargo"),
            flex: 0.8,
            minWidth: 120,
            width: widths.aceitacarga,
            sortable: false,
            renderCell: (params) => <span>{params.value}</span>,
          },
          {
            field: "distancia",
            headerName: t("trechoTable.colDistance"),
            flex: 0.7,
            minWidth: 110,
            width: widths.distancia,
            renderCell: (params) => <span>{params.value}</span>,
          },
        ]
      : []),

    // Data da última atualização
    {
      field: "data_ultima_alteracao",
      headerName: t("table.colUltimaAtualizacao"),
      flex: 1,
      minWidth: 180,
      width: widths.data_ultima_alteracao,
      renderCell: (params) => <span>{fmtDateTime(params.value)}</span>,
    },

    // Estoque
    {
      field: "estoque",
      headerName: t("table.colEstoque"),
      flex: 0.8,
      minWidth: 120,
      width: widths.estoque,
      renderCell: (params) => {
        if (!massEditOn || !canEditStock)
          return <span>{params.row.estoque ?? "—"}</span>;
        const d = drafts[params.id] || {};
        return (
          <TextField
            {...intInput}
            value={d.estoque ?? ""}
            onChange={(e) =>
              setDrafts((prev) => ({
                ...prev,
                [params.id]: { ...prev[params.id], estoque: e.target.value },
              }))
            }
          />
        );
      },
    },

    // Status
    {
      field: "status",
      headerName: t("table.colStatus"),
      flex: 0.7,
      minWidth: 120,
      width: widths.status,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isActive = Number(params.row.status) === 1;
        const label = statusLabelCell(params.row.status);
        if (!massEditOn) {
          return (
            <Chip
              size="small"
              label={label}
              color={isActive ? "success" : "default"}
              variant="outlined"
            />
          );
        }
        const d = drafts[params.id] || {};
        const checked = Number(d.status ?? params.row.status) === 1;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={checked}
              size="small"
              onChange={(e) =>
                setDrafts((prev) => ({
                  ...prev,
                  [params.id]: {
                    ...prev[params.id],
                    status: e.target.checked ? 1 : 0,
                  },
                }))
              }
            />
            <Chip
              size="small"
              label={statusLabelCell(d.status ?? params.row.status)}
              color={checked ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        );
      },
    },

    // Destaque
    {
      field: "destaque",
      headerName: t("table.colDestaque"),
      flex: 0.6,
      minWidth: 110,
      width: widths.destaque,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        Number(params.value) === 1 ? (
          <StarRoundedIcon fontSize="small" color="warning" />
        ) : (
          <span style={{ opacity: 0.5 }}>—</span>
        ),
    },

    // Cadastro
    {
      field: "data_cadastro",
      headerName: t("table.colCadastro"),
      flex: 1,
      minWidth: 150,
      width: widths.data_cadastro,
    },

    // Peso e dimensões
    {
      field: "peso",
      headerName: t("table.colPeso"),
      flex: 0.8,
      minWidth: 120,
      width: widths.peso,
      valueFormatter: (params) => fmtKg(params?.value),
    },
    {
      field: "largura",
      headerName: t("table.colLargura"),
      flex: 0.8,
      minWidth: 130,
      width: widths.largura,
      valueFormatter: (params) => fmtCm(params?.value),
    },
    {
      field: "altura",
      headerName: t("table.colAltura"),
      flex: 0.8,
      minWidth: 120,
      width: widths.altura,
      valueFormatter: (params) => fmtCm(params?.value),
    },
    {
      field: "comprimento",
      headerName: "Comp. (cm)",
      flex: 0.9,
      minWidth: 135,
      width: widths.comprimento,
      valueFormatter: (params) => fmtCm(params?.value),
    },

    // Ações
    {
      field: "actions",
      headerName: t("table.colAcoes"),
      flex: 1,
      minWidth: 170,
      width: widths.actions,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center">
          {canUpdateProduct && (
            <Tooltip title={t("table.editFullForm")}>
              <IconButton
                size="small"
                onClick={() => onEditProduct?.(params.row.originalProduct)}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {canDeleteProduct && (
            <Tooltip title={t("table.delete")}>
              <IconButton
                size="small"
                onClick={() => onDeleteProduct?.(params.row.id)}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  /* ===== Controle de visibilidade dinâmica para Empresa ===== */
  const [multiProject, setMultiProject] = useState(false);
  useEffect(() => {
    // considera multi quando houver +1 projeto no filtro
    const ids = Array.isArray(filters?.ids_projetos)
      ? filters.ids_projetos
      : typeof filters?.ids_projetos === "string"
      ? String(filters.ids_projetos)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    setMultiProject(ids.length > 1);
  }, [filters?.ids_projetos]);

  // Montagem final de colunas: injeta "Empresa" no início quando multiProject
  const baseColumnsWithEmpresa = [empresaColumn, ...baseColumns];

  // visibilidade efetiva respeitando o localStorage,
  // mas incluindo automaticamente "projeto_nome" quando multiProject
  const effectiveVisible = useMemo(() => {
    const current = new Set(visibleColumns);
    if (multiProject) current.add("projeto_nome");
    return Array.from(current);
  }, [visibleColumns, multiProject]);

  const columns = baseColumnsWithEmpresa.filter((c) =>
    effectiveVisible.includes(c.field)
  );

  /* ===== Menu de colunas ===== */
  const [colsMenuEl, setColsMenuEl] = useState(null);
  const toggleColumn = (field) => {
    setVisibleColumns((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  /* ===== Filtros Avançados ===== */
  const filtersDefault = isPassengerTransport ? FILTERS_DEFAULT_PASSENGER : FILTERS_DEFAULT;
  const [localFilters, setLocalFilters] = useState(filtersDefault);
  const [localAppliedFilters, setLocalAppliedFilters] = useState(null);

  useEffect(() => {
    setLocalFilters(filtersDefault);
    setLocalAppliedFilters(null);
  }, [isPassengerTransport]); // eslint-disable-line react-hooks/exhaustive-deps

  const useLocalFallback = !onFiltersApply;
  const effFilters = filters ?? localFilters;

  const patchFilters = (patch) => {
    // se categoria mudar, limpar subcategoria se não pertencer
    if (Object.prototype.hasOwnProperty.call(patch, "id_categoria")) {
      const nextCat = patch.id_categoria;
      const sub = (filters ?? localFilters).id_subcategoria;
      const validSub = getSubcatsOf(nextCat).some(
        (s) => String(s.id) === String(sub)
      );
      if (!validSub) patch.id_subcategoria = "";
    }

    if (typeof onFiltersChange === "function") {
      onFiltersChange(patch);
    } else {
      setLocalFilters((prev) => ({ ...prev, ...patch }));
    }
  };

  const getF = (k, def = "") =>
    Object.prototype.hasOwnProperty.call(effFilters, k) ? effFilters[k] : def;

  const parseNumber = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const applyFilters = (rws, f) => {
    if (!f) return rws;
    return rws.filter((row) => {
      const p = row.originalProduct || {};
      if (f.status !== "all") {
        const s = Number(f.status);
        if (Number(row.status) !== s) return false;
      }

      if (isPassengerTransport) {
        if (f.fonte && String(p.fonte || "").trim().toLowerCase() !== String(f.fonte).toLowerCase()) {
          return false;
        }
        if (f.id_origem && String(p.id_origem ?? "") !== String(f.id_origem)) return false;
        if (f.id_destino && String(p.id_destino ?? "") !== String(f.id_destino)) return false;
        if (f.destaque !== "all") {
          const d = Number(f.destaque);
          if (Number(p.destaque ? 1 : 0) !== d) return false;
        }
        if (f.aceitacarga !== "all") {
          const a = Number(f.aceitacarga);
          if (Number(p.aceitacarga ? 1 : 0) !== a) return false;
        }
        const pmin = parseNumber(f.precoMin);
        const pmax = parseNumber(f.precoMax);
        if (pmin != null && Number(row.preco ?? 0) < pmin) return false;
        if (pmax != null && Number(row.preco ?? 0) > pmax) return false;
        if (f.id_categoria && String(p.id_categoria ?? "") !== String(f.id_categoria)) return false;
        return true;
      }

      if (f.tipo !== "all") {
        const tipoVal = Number(f.tipo);
        if (Number(p.tipo) !== tipoVal) return false;
      }
      const pmin = parseNumber(f.precoMin);
      const pmax = parseNumber(f.precoMax);
      if (pmin != null && Number(row.preco ?? 0) < pmin) return false;
      if (pmax != null && Number(row.preco ?? 0) > pmax) return false;
      const emin = parseNumber(f.estoqueMin);
      const emax = parseNumber(f.estoqueMax);
      if (emin != null && Number(row.estoque ?? 0) < emin) return false;
      if (emax != null && Number(row.estoque ?? 0) > emax) return false;
      if (f.id_categoria && String(p.id_categoria ?? "") !== String(f.id_categoria)) return false;
      if (f.id_subcategoria && String(p.id_subcategoria ?? "") !== String(f.id_subcategoria)) return false;
      return true;
    });
  };

  const rowsFilteredLocal = useMemo(
    () => applyFilters(rows, localAppliedFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, localAppliedFilters]
  );

  const rowsToShow =
    useLocalFallback && localAppliedFilters ? rowsFilteredLocal : rows;
  const rowCountToShow =
    useLocalFallback && localAppliedFilters ? rowsFilteredLocal.length : rowCount;

  // Opções dos selects de categorias/subcategorias
  const categoryOptions = useMemo(
    () =>
      (rootCategories || []).map((c) => ({
        value: String(c.id),
        label: c.nome,
      })),
    [rootCategories]
  );

  const subcategoryOptions = useMemo(() => {
    const catId = getF("id_categoria", "");
    const subs = catId ? getSubcatsOf(catId) : [];
    return subs.map((s) => ({ value: String(s.id), label: s.nome }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootCategories, effFilters.id_categoria]);

  const cityFilterOptions = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      if (p.id_origem) {
        const id = String(p.id_origem);
        if (!map.has(`o-${id}`)) {
          map.set(`o-${id}`, {
            value: id,
            label: formatCityCell(p.origem_nome, p.origem_uf) || id,
          });
        }
      }
      if (p.id_destino) {
        const id = String(p.id_destino);
        if (!map.has(`d-${id}`)) {
          map.set(`d-${id}`, {
            value: id,
            label: formatCityCell(p.destino_nome, p.destino_uf) || id,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [products]);

  const fonteFilterOptions = useMemo(() => {
    const set = new Set();
    (products || []).forEach((p) => {
      const f = String(p.fonte || "").trim();
      if (f) set.add(f);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((f) => ({ value: f, label: f }));
  }, [products]);

  const statusFilterField = {
    key: "status",
    label: t("table.colStatus"),
    type: "select",
    value: getF("status", "all"),
    onChange: (v) => patchFilters({ status: v }),
    options: [
      { value: "all", label: t("trechoFilters.all") },
      { value: "1", label: t("table.statusActive") },
      { value: "0", label: t("table.statusInactive") },
    ],
  };

  const genericFilterFields = [
    statusFilterField,
    {
      key: "tipo",
      label: t("table.colTipo"),
      type: "select",
      value: getF("tipo", "all"),
      onChange: (v) => patchFilters({ tipo: v }),
      options: [
        { value: "all", label: t("trechoFilters.all") },
        { value: "1", label: t("table.tipo1") },
        { value: "2", label: t("table.tipo2") },
        { value: "3", label: t("table.tipo3") },
        { value: "4", label: t("table.tipo4") },
        { value: "5", label: t("table.tipo5") },
      ],
    },
    {
      key: "precoMin",
      label: t("trechoFilters.priceMin"),
      type: "number",
      value: getF("precoMin", ""),
      onChange: (v) => patchFilters({ precoMin: v }),
      inputProps: { step: "0.01", min: 0 },
    },
    {
      key: "precoMax",
      label: t("trechoFilters.priceMax"),
      type: "number",
      value: getF("precoMax", ""),
      onChange: (v) => patchFilters({ precoMax: v }),
      inputProps: { step: "0.01", min: 0 },
    },
    {
      key: "estoqueMin",
      label: t("trechoFilters.stockMin"),
      type: "number",
      value: getF("estoqueMin", ""),
      onChange: (v) => patchFilters({ estoqueMin: v }),
      inputProps: { step: "1", min: 0 },
    },
    {
      key: "estoqueMax",
      label: t("trechoFilters.stockMax"),
      type: "number",
      value: getF("estoqueMax", ""),
      onChange: (v) => patchFilters({ estoqueMax: v }),
      inputProps: { step: "1", min: 0 },
    },
    {
      key: "id_categoria",
      label: t("table.colCategoria"),
      type: "select",
      value: getF("id_categoria", ""),
      onChange: (v) => patchFilters({ id_categoria: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...categoryOptions],
    },
    {
      key: "id_subcategoria",
      label: t("table.colSubcategoria"),
      type: "select",
      value: getF("id_subcategoria", ""),
      onChange: (v) => patchFilters({ id_subcategoria: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...subcategoryOptions],
    },
  ];

  const passengerFilterFields = [
    statusFilterField,
    {
      key: "id_origem",
      label: t("trechoTable.colOrigin"),
      type: "select",
      value: getF("id_origem", ""),
      onChange: (v) => patchFilters({ id_origem: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...cityFilterOptions],
    },
    {
      key: "id_destino",
      label: t("trechoTable.colDestination"),
      type: "select",
      value: getF("id_destino", ""),
      onChange: (v) => patchFilters({ id_destino: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...cityFilterOptions],
    },
    {
      key: "fonte",
      label: t("trechoTable.colSource"),
      type: "select",
      value: getF("fonte", ""),
      onChange: (v) => patchFilters({ fonte: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...fonteFilterOptions],
    },
    {
      key: "destaque",
      label: t("table.colDestaque"),
      type: "select",
      value: getF("destaque", "all"),
      onChange: (v) => patchFilters({ destaque: v }),
      options: [
        { value: "all", label: t("trechoFilters.all") },
        { value: "1", label: t("trechoFilters.featuredOnly") },
        { value: "0", label: t("trechoFilters.notFeatured") },
      ],
    },
    {
      key: "aceitacarga",
      label: t("trechoTable.colAcceptsCargo"),
      type: "select",
      value: getF("aceitacarga", "all"),
      onChange: (v) => patchFilters({ aceitacarga: v }),
      options: [
        { value: "all", label: t("trechoFilters.all") },
        { value: "1", label: t("trechoTable.yes") },
        { value: "0", label: t("trechoTable.no") },
      ],
    },
    {
      key: "precoMin",
      label: t("trechoFilters.referencePriceMin"),
      type: "number",
      value: getF("precoMin", ""),
      onChange: (v) => patchFilters({ precoMin: v }),
      inputProps: { step: "0.01", min: 0 },
    },
    {
      key: "precoMax",
      label: t("trechoFilters.referencePriceMax"),
      type: "number",
      value: getF("precoMax", ""),
      onChange: (v) => patchFilters({ precoMax: v }),
      inputProps: { step: "0.01", min: 0 },
    },
    {
      key: "id_categoria",
      label: t("table.colCategoria"),
      type: "select",
      value: getF("id_categoria", ""),
      onChange: (v) => patchFilters({ id_categoria: v }),
      options: [{ value: "", label: t("trechoFilters.allFeminine") }, ...categoryOptions],
    },
  ];

  const advancedFilters = {
    fields: isPassengerTransport ? passengerFilterFields : genericFilterFields,
    onApply: () => {
      if (typeof onFiltersApply === "function") {
        onFiltersApply({ ...(filters ?? localFilters) });
      } else {
        setLocalAppliedFilters({ ...localFilters });
      }
    },
    onClear: () => {
      if (typeof onFiltersClear === "function") onFiltersClear();
      else {
        setLocalFilters(filtersDefault);
        setLocalAppliedFilters(null);
      }
    },
  };

  const advancedFiltersActiveCount = useMemo(() => {
    const f = useLocalFallback ? localAppliedFilters : filters ?? localFilters;
    if (!f) return 0;
    const defaults = isPassengerTransport ? FILTERS_DEFAULT_PASSENGER : FILTERS_DEFAULT;
    return Object.keys(defaults).filter((k) => {
      const v = f[k];
      if (v === "" || v == null) return false;
      if ((k === "status" || k === "destaque" || k === "aceitacarga") && v === "all") return false;
      return String(v) !== String(defaults[k]);
    }).length;
  }, [useLocalFallback, localAppliedFilters, filters, localFilters, isPassengerTransport]);

  /* ===== TopBar ===== */
  const editLabel = useMemo(() => {
    if (isPassengerTransport) {
      if (canEditPrice) return t("trechoTable.editPriceStatus");
      return t("table.edit");
    }
    if (canEditPrice && canEditStock) return t("table.editPriceStockStatus");
    if (canEditPrice) return t("table.editPriceStatus");
    if (canEditStock) return t("table.editStockStatus");
    return t("table.edit");
  }, [canEditPrice, canEditStock, isPassengerTransport, t]);

  const showMassEditToggle = isPassengerTransport ? canEditPrice : canEditAnyPriceStock;

  const TopBar = (
    <Box
      sx={{
        mb: 1.5,
        display: "flex",
        gap: 1,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      {showMassEditToggle && (
        <FormControlLabel
          control={
            <Switch
              checked={massEditOn}
              onChange={(e) => setMassEditOn(e.target.checked)}
            />
          }
          label={editLabel}
        />
      )}

      {massEditOn && (
        <>
          <Tooltip
            title={hasChanges ? t("table.saveChanges") : t("table.saveNoChanges")}
          >
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<Save />}
                onClick={() => setConfirmOpen(true)}
                disabled={!hasChanges}
              >
                {t("table.saveChanges")}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<Close />}
            onClick={() => setMassEditOn(false)}
          >
            {t("table.cancel")}
          </Button>
        </>
      )}

      <Box sx={{ flex: 1 }} />
      <Tooltip title={t("table.chooseColumns")}>
        <IconButton onClick={(e) => setColsMenuEl(e.currentTarget)} size="small">
          <ViewColumnRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={colsMenuEl}
        open={Boolean(colsMenuEl)}
        onClose={() => setColsMenuEl(null)}
      >
        {columnCatalog.map((col) => (
          <MenuItem
            key={col.field}
            onClick={() => toggleColumn(col.field)}
            dense
            disabled={
              isPassengerTransport
                ? col.field === "origem_nome" || col.field === "destino_nome"
                : col.field === "nome"
            }
          >
            <Checkbox
              checked={
                visibleColumns.includes(col.field) ||
                (col.field === "projeto_nome" && multiProject)
              }
            />
            <ListItemText primary={menuColumnLabel(col.field)} />
          </MenuItem>
        ))}
        <Box sx={{ display: "flex", gap: 1, px: 1.5, pb: 1 }}>
          <Button
            size="small"
            onClick={() => setVisibleColumns(columnCatalog.map((c) => c.field))}
          >
            {t("table.all")}
          </Button>
          <Button size="small" onClick={() => setVisibleColumns(defaultVisible)}>
            {t("table.default")}
          </Button>
        </Box>
      </Menu>
    </Box>
  );

  /* ===== Confirmação em massa (só diffs) ===== */
  const renderDiffLines = (r) => {
    const diffs = [];
    if (r.after.preco !== r.before.preco) {
      diffs.push(
        <Typography key="preco" variant="body2">
          <strong>{t("table.diffPrecoSugerido")}</strong> {toBRL(r.before.preco)} → {toBRL(r.after.preco)}
        </Typography>
      );
    }
    if (r.after.preco_comdesconto !== r.before.preco_comdesconto) {
      diffs.push(
        <Typography key="final" variant="body2">
          <strong>{t("table.diffPrecoConc")}</strong>{" "}
          {toBRL(r.before.preco_comdesconto)} → {toBRL(r.after.preco_comdesconto)}
        </Typography>
      );
    }
    if (r.after.estoque !== r.before.estoque) {
      diffs.push(
        <Typography key="stock" variant="body2">
          <strong>{t("table.diffEstoque")}</strong> {r.before.estoque ?? "—"} → {r.after.estoque ?? "—"}
        </Typography>
      );
    }
    if (r.after.status !== r.before.status) {
      diffs.push(
        <Typography key="status" variant="body2">
          <strong>{t("table.statusCompare")}:</strong> {statusLabelCell(r.before.status)} →{" "}
          {statusLabelCell(r.after.status)}
        </Typography>
      );
    }
    return diffs;
  };

  return (
    <>
      {TopBar}

      <GenericTable
        rows={rowsToShow}
        columns={columns}
        rowCount={rowCountToShow}
        pageSize={pageSize}
        page={page}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSearch={onSearch}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        isLoading={isLoading || categoriesLoading}
        onExportPDFTitle={t("table.exportSelectedTitle")}
        exportFileName={isPassengerTransport ? "trechos" : "produtos"}
        onColumnWidthChange={handleColumnWidthChange}
        advancedFilters={advancedFilters}
        advancedFiltersActiveCount={advancedFiltersActiveCount}
        onDeleteSelected={onDeleteSelected}
        sortingMode={sortingMode}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        // Clique na linha só abre edição se tiver permissão de update
        onRowClick={canUpdateProduct ? ((row) => onEditProduct?.(row.originalProduct || row)) : undefined}
      />

      {/* Confirmação em massa */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("table.confirmChanges")}</DialogTitle>
        <DialogContent dividers>
          {changedRows.length === 0 ? (
            <Typography variant="body2">
              {t("table.noChanges")}
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {changedRows.map((r) => (
                <Box key={r.id}>
                  <Typography variant="subtitle2" noWrap title={r.nome}>
                    {r.nome}
                  </Typography>
                  <Stack sx={{ pl: 0.5 }} spacing={0.25}>
                    {renderDiffLines(r)}
                  </Stack>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`ID ${r.id}`} />
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>
            {t("table.back")}
          </Button>
          <Button
            variant="contained"
            onClick={applyChanges}
            disabled={changedRows.length === 0 || applying}
          >
            {applying ? t("table.applying") : t("table.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resultado detalhado */}
      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ open: false, results: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("table.resultTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {resultDialog.results.map((r) => (
              <Box key={r.id}>
                <Typography variant="subtitle2">{r.nome}</Typography>
                {r.ok ? (
                  <Typography variant="body2" color="success.main">
                    {t("table.updatedOk")}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="error.main">
                    {t("table.errorWithDetail", { prefix: t("table.errorPrefix"), detail: r.error })}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialog({ open: false, results: [] })}>
            {t("table.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar curto */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductTable;
